import { stringHasURL } from "shared/helpers/url-helper";
import SocialList from "../quiz/SocialList";
import { LoadingOutlined } from "@ant-design/icons";
import {
  fetchInstagramList,
  fetchSpotifyArtists,
  fetchYoutubeArtists,
} from "shared/service/StrmService";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import "./NewAssessment.scss";
import snakeToCamelCase from "shared/helpers/snakeToCamelCase";
import {
  fetchArtistByName,
  fetchArtistRankingDetail,
} from "shared/service/RadarService";
import { useCallback, useEffect, useState } from "react";
import { Col, Form, Input, Select, Spin } from "antd";
import SingleMultiselect, {
  filterOptionDiacritics,
} from "shared/components/single-multiselect/SingleMultiselect";
import { useSelector } from "react-redux";
import PhoneWithDDD from "shared/components/phone-with-ddd/PhoneWithDDD";
import { validatePhone } from "shared/helpers/phone-helpers";
import { QUIZ_INFO } from "shared/constants/StorageConsts";
import { LanguageChange } from "shared/components";
import { RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInstagram,
  faSpotify,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import {
  onClickInstagram,
  onClickSpotify,
  onClickYoutube,
} from "shared/helpers/external-url-helper";

const NewAssessment: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedArtist, setSelectedArtist] = useState<any>();
  const [artistProfile, setArtistProfile] = useState<any>(null);
  const { genreOptions } = useSelector((state: any) => state.genres);
  const { countryOptions } = useSelector((state: any) => state.countries);
  const [loading, setLoading] = useState<boolean>(false);

  const getArtists = async (artistNameTerm: string) => {
    if (artistNameTerm) {
      const response = await fetchArtistByName({ artistNameTerm });
      const artists = response.map((artist: any) => {
        return {
          id: artist.artistIdStrm,
          artistIdStrm: artist.artistIdStrm,
          name: artist.artistName,
          avatarUrl: artist?.imagesSpotify?.[0]?.url,
        };
      });

      return artists;
    }
  };

  const getArtistInfo = useCallback(async () => {
    if (
      selectedArtist?.spotifyId ||
      selectedArtist?.youtubeId ||
      selectedArtist?.instagramId
    ) {
      const artists = await getArtists(selectedArtist?.name);
      if (artists?.length > 0) {
        const profile = artists.find(
          (artist: any) =>
            artist.id === selectedArtist?.id ||
            artist.spotifyId === selectedArtist?.spotifyId ||
            artist.youtubeId === selectedArtist?.youtubeId ||
            artist.instagramId === selectedArtist?.instagramId
        );
        console.log(profile);
        if (!profile) {
          setSelectedArtist(undefined);
          return;
        }
        setSelectedArtist({
          name: profile.name,
          avatarUrl: profile.avatarUrl,
          id: profile.id,
        });
      }
      return;
    }
    if (selectedArtist?.id) {
      setLoading(true);
      const response: any = await fetchArtistRankingDetail(selectedArtist?.id);
      if (!response) return;
      const profile = snakeToCamelCase(response);
      setArtistProfile({
        name: profile.artistMetadata.artistName,
        genreCodes: profile.artistMetadata.genresStrm,
        countryCode: profile.artistMetadata.country,
        countryCodePhone: profile.artistMetadata.country,
        artistIdStrm: profile.artistMetadata.artistIdStrm,
        spotify: { id: profile.artistMetadata.artistIdSpotify },
        youtube: { id: profile.artistMetadata.channelIdYoutube },
        instagram: { id: profile.artistMetadata.usernameInstagram },
      });
      setLoading(false);
      return;
    }

    setArtistProfile(undefined);
  }, [selectedArtist]);

  const getSpotifyArtists = async (term: string, offset: number = 0) => {
    const artists: any = await fetchSpotifyArtists(
      encodeURIComponent(term),
      "BR", //check rule
      offset
    );
    const artistsOption = artists.map(({ name, id, ...artist }: any) => ({
      name,
      id,
      avatarUrl: artist?.images[0]?.url,
      spotifyId: id,
    }));
    if (artistsOption.length === 0) {
      return [
        {
          name: t("quiz.pages.Questionario.spotifyNotFound"),
        },
      ];
    }
    return artistsOption;
  };

  const getYoutubeArtists = async (term: string) => {
    const artists: any = await fetchYoutubeArtists(encodeURIComponent(term));
    const artistsOption = artists.map((artist: any) => {
      return {
        id: artist.snippet?.channelId,
        youtubeId: artist.snippet?.channelId,
        name: artist.snippet?.title,
        avatarUrl: artist.snippet?.thumbnails?.medium?.url,
      };
    });
    if (artistsOption.length === 0) {
      return [
        {
          name: t("quiz.pages.Questionario.youtubeNotFound"),
        },
      ];
    } else return artistsOption;
  };

  const getInstagramArtists = async (term: string) => {
    try {
      const response = await fetchInstagramList(
        encodeURIComponent(term?.trim())
      );
      const profileList = snakeToCamelCase(response);
      const result = profileList.map((profile: any) => {
        return {
          id: profile.searchByUsername,
          instagramId: profile.searchByUsername,
          name: profile.profile?.artistName,
          avatarUrl: profile.profile?.pictureUrl,
        };
      });
      if (profileList.length === 1) {
        const profile = profileList[0];
        if (!profile.profileFound || !profile.serviceAvailable) {
          return [
            {
              id: "no-data",
              name: t("quiz.pages.Questionario.instagramUserNotFound"),
            },
          ];
        }
      }

      return result;
    } catch (error) {
      return [
        {
          name: t("quiz.pages.Questionario.instagramUserNotFound"),
          id: "no-data",
        },
      ];
    }
  };

  const getArtistProfile = async (term: string, offset: number = 0) => {
    if (term) {
      if (stringHasURL(term)) {
        if (term.includes("spotify")) {
          return await getSpotifyArtists(term, offset);
        }
        if (term.includes("youtube")) {
          return await getYoutubeArtists(term);
        }
        if (term.includes("instagram")) {
          return await getInstagramArtists(term);
        }
      } else {
        return await getArtists(term);
      }
    } else {
      return [];
    }
  };

  const onFinish = () => {
    const values = form.getFieldsValue(true);
    sessionStorage.setItem(
      QUIZ_INFO,
      JSON.stringify({
        ...values,
      })
    );
    history.push("/cadastre", { quiz: true });
  };

  const onClickSocial = (social: string) => {
    const socialId = artistProfile?.[social]?.id;
    if (socialId) {
      switch (social) {
        case "spotify":
          onClickSpotify(socialId);

          break;
        case "youtube":
          onClickYoutube(socialId);
          break;
        case "instagram":
          onClickInstagram(socialId);
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    getArtistInfo();
  }, [getArtistInfo]);

  useEffect(() => {
    if (artistProfile) {
      form.resetFields();
    }
  }, [artistProfile, form]);

  return (
    <div
      style={{
        minWidth: isMobile ? 300 : 600,
        padding: "0 20px",
        position: "relative",
      }}
      className="new-assessment"
    >
      <div style={{ position: "absolute", right: 0 }}>
        <LanguageChange.SelectLanguageChange
          placement="bottomRight"
          dropdownStyle={{ zIndex: 999999, position: "fixed" }}
          width={60}
        />
      </div>{" "}
      <h1>New Assessment</h1>
      <SocialList
        label="Artista"
        placeholder="Digite o nome do projeto artistico"
        getListOptions={getArtistProfile}
        showMore
        showDefaultImage={false}
        onChange={(value: any) => {
          if (value) {
            const { id, spotifyId, youtubeId, instagramId, ...rest } = value;
            if (spotifyId || youtubeId || instagramId) {
              setSelectedArtist(rest);
            } else {
              setSelectedArtist(value);
            }
          } else {
            setSelectedArtist(undefined);
            setArtistProfile(undefined);
          }
        }}
        offsetTop={-62}
        bottomDefault={44}
        value={selectedArtist}
      />
      <Form
        initialValues={artistProfile}
        layout="vertical"
        form={form}
        onFinish={onFinish}
      >
        {loading && (
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 20 }}
          >
            <Spin
              indicator={
                <LoadingOutlined style={{ fontSize: 78 }} spin={false} />
              }
            />
          </div>
        )}

        {artistProfile && (
          <>
            <h1>Esse é você?</h1>
            <h3>Por favor confirme seus dados e clique em avançar</h3>
            <div className="social-icons">
              <FontAwesomeIcon
                icon={faSpotify}
                className="label-icon"
                onClick={() => onClickSocial("spotify")}
              />
              <FontAwesomeIcon
                icon={faYoutube}
                className="label-icon"
                onClick={() => onClickSocial("youtube")}
              />
              <FontAwesomeIcon
                icon={faInstagram}
                className="label-icon"
                onClick={() => onClickSocial("instagram")}
              />
            </div>
            <Col xs={24}>
              <Form.Item
                label="Projeto Artístico"
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("quiz.pages.Questionario.nameRequired"),
                  },
                ]}
              >
                <Input
                  placeholder={t("quiz.pages.Questionario.typeHerePlaceholder")}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label={t("quiz.pages.Questionario.genreLabel")}
                name="genreCodes"
                colon={false}
                rules={[
                  {
                    required: true,
                    message: t("quiz.pages.Questionario.genreRequired"),
                  },
                ]}
              >
                <SingleMultiselect
                  options={genreOptions}
                  optionFilterProp="label"
                  placeholder={t(
                    "quiz.pages.Questionario.selectHerePlaceholder"
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label={t("quiz.pages.Questionario.countryLabel")}
                colon={false}
                name="countryCode"
                rules={[
                  {
                    required: true,
                    message: t("quiz.pages.Questionario.countryRequired"),
                  },
                ]}
              >
                <Select
                  showSearch
                  options={countryOptions}
                  filterOption={filterOptionDiacritics}
                  notFoundContent={t("quiz.pages.Questionario.noItem")}
                  placeholder={t(
                    "quiz.pages.Questionario.selectHerePlaceholder"
                  )}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label={t("quiz.pages.Questionario.phoneLabel")}>
                <PhoneWithDDD
                  names={{
                    ddi: "ddi",
                    phoneCountry: "countryCodePhone",
                    phone: "phone",
                  }}
                  rules={{
                    ddi: [
                      {
                        required: true,
                        message: t("quiz.pages.Questionario.dddRequired"),
                      },
                    ],
                    phone: [
                      {
                        required: true,
                        message: t("quiz.pages.Questionario.phoneRequired"),
                      },
                      {
                        validator(_: any, value: any) {
                          const { countryCodePhone } = form.getFieldsValue();
                          return validatePhone(
                            countryCodePhone,
                            value,
                            t("pages.MeusDados.telefoneInvalido")
                          );
                        },
                      },
                    ],
                  }}
                />
              </Form.Item>
            </Col>

            <button className="botao-roxo continuar" style={{ width: "100%" }}>
              {t("quiz.pages.Questionario.confirm")}
            </button>
          </>
        )}
      </Form>
    </div>
  );
};

export default NewAssessment;
